import {
    isValidElement,
    cloneElement,
    type FC
} from "react";
import {
    type ViewStyle,
    StyleSheet,
    View
} from "react-native";
import type IPopoverTriggerProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePopoverContext
} from "../../context";
import type {
    PopoverTriggerChildPropsType,
    PopoverTriggerElementType
} from "../../type";
import {
    getStateColorKey
} from "../../../../helpers/theme/state";
import StateLayer from "../../../stateLayer";
import Button from "../../../button";

const PopoverTrigger: FC<IPopoverTriggerProps> = ({
    customTheme: customThemeProp,
    children,
    style
}) => {
    const context = usePopoverContext({
        part: "Trigger"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        radiuses,
        borders
    } = NCoreUIKitTheme.useContext(customTheme);

    if(!context) {
        return null;
    }

    const {
        handleToggle,
        triggerRef,
        contentId,
        isOpen
    } = context;

    const childElement: PopoverTriggerElementType = isValidElement<PopoverTriggerChildPropsType>(children) ? children : <Button
        title={typeof children === "string" ? children : undefined}
        customTheme={customTheme}
        onPress={handleToggle}
        variant="outline"
        type="neutral"
        size="small"
    />;

    const isButton = childElement.type === Button;

    const isDisabled = childElement.props.isDisabled === true;

    const isCustomChild = isValidElement(children);

    const handlePress = () => {
        if(isCustomChild) {
            childElement.props.onPress?.();
        }

        handleToggle();
    };

    const triggerProps: PopoverTriggerChildPropsType = {
        "aria-controls": isOpen ? contentId : undefined,
        "aria-haspopup": "dialog",
        "aria-expanded": isOpen,
        onPress: handlePress
    };

    const renderOpenLayer = () => {
        if(!isButton || !isOpen || isDisabled) {
            return null;
        }

        const isFilled = childElement.props.variant === undefined || childElement.props.variant === "filled";

        return <StateLayer
            borderRadius={childElement.props.borderRadius ?? radiuses.actions}
            overlayColor={isFilled ? "default" : getStateColorKey({
                type: childElement.props.type ?? "primary"
            })}
            borderWidth={borders.line}
            customTheme={customTheme}
            isPressed={true}
        />;
    };

    const flatStyle = StyleSheet.flatten<ViewStyle>(style);

    const isAnchorStretched = flatStyle?.alignSelf === "stretch" || flatStyle?.width !== undefined;

    return <View
        style={style}
    >
        <View
            style={[
                stylesheet.anchor,
                isAnchorStretched ? stylesheet.stretchedAnchor : undefined
            ]}
            collapsable={false}
            ref={triggerRef}
        >
            {
                cloneElement(childElement, triggerProps)
            }
            {renderOpenLayer()}
        </View>
    </View>;
};
export default PopoverTrigger;
